import React, { forwardRef, ElementType, Ref } from 'react';
import classNames from 'classnames';
import { COLORS } from '../../_internal/colors';
import Box, { BoxProps } from '../../Box';
import Button from '../../Button';
import CloudinaryImage, { CloudinaryImageProps } from '../../CloudinaryImage';
import Stack, { StackProps } from '../../Stack';
import Text, { TextProps } from '../../Text';
import styles from './CategoryCardBase.module.scss';
import { IconArrowRight16 } from '../../icons';
import Inline from '../../Inline';

const DEFAULT_ICON_SPACING = 0.25;

export type CategoryCardVariant = 'icon' | 'logo' | 'editorial' | 'text';

export type CategoryCardBaseProps = {
  /**
   * The link element type for both image and text.
   * @default 'a'
   */
  linkAs?: ElementType;
  /**
   * The card style and layout. Each variant is configured to display data according to a style and layout defined in `variantLayouts`.
   */
  variant: CategoryCardVariant;
  /**
   * The URL that the component should link to (i.e. Category Listing).
   */
  href: string;
  /**
   * Callback to execute when a category is clicked. Helpful for event tracking.
   */
  onClick?: () => void;
  /**
   * Image for the Card with the appropriate format and aspect ratio.
   *
   * - `editorial`: 4:5 image
   * - `icon`: 1:1 image
   * - `logo`: 5:3 transparent svg/png
   * - `text`: null
   */
  image?:
    | {
        /**
         * The alt text for the image.
         */
        altText: string;
        /**
         * The base URL for an image hosted on Cloudinary (or Cloudfront CDN).
         *
         * Cloudinary example: `https://res.cloudinary.com/stitch-fix/image/upload/`
         *
         * Cloudfront example: `https://d3n78nkjl8tizo.cloudfront.net/stitch-fix/image/upload/`
         */
        baseUrl: string;
        /**
         * A Cloudinary image's name (version and/or public id)
         *
         * Example: `/v1585276216/a6ttgw2xjomdl1pbzehz.jpg`
         */
        mediaPath: string;
      }
    | JSX.Element;
  /**
   * Text used to display the `editorial` and `icon` category title.
   */
  title?: string;
  /**
   * Text used to display the `editorial` long or short description.
   * Text used to display the `text` category title.
   */
  description?: string;
  /**
   * Text used to display the `editorial` tagline.
   */
  tagline?: string;
  /**
   * If provided, it is used to display the `editorial` cta button.
   */
  cta?: {
    href: string;
    text: string;
  };
};

// Layout provides configurable image and styling properties for the layout of the different card variants.
interface Layout {
  verticalSpacing: StackProps['spacing'];
  sizes: CloudinaryImageProps['sizes'];
  copyStyles?: React.CSSProperties;
  imageStyles?: React.CSSProperties;
  textTitleProps: TextProps;
  copyProps?: BoxProps;
}
/**
 * This function returns the layout configuration for each card variant based on variant name and theme.
 */
const variantLayoutBasedOnTheme = (variant: CategoryCardVariant): Layout => {
  switch (variant) {
    case 'icon':
      return {
        verticalSpacing: 0,
        sizes: { sm: 200, lg: 300 },
        copyStyles: { textAlign: 'center', marginRight: '-0.25rem' },
        imageStyles: {
          borderRadius: '8px',
          overflow: 'hidden',
        },
        textTitleProps: {
          height: 'short',
          family: 'medium',
          scale: '3',
        },
        copyProps: {
          px: 0.25,
          pt: 0.5,
        },
      };

    case 'logo':
      return {
        verticalSpacing: 0,
        sizes: { sm: 200, lg: 300 },
        imageStyles: {
          border: `solid 1px ${COLORS['gray-92']}`,
          background: COLORS['gray-99'],
        },
        textTitleProps: {
          setting: 'title-xsmall',
        },
      };
    case 'editorial':
      return {
        verticalSpacing: 0.5,
        sizes: { sm: 600 },
        imageStyles: {
          borderRadius: '8px',
          overflow: 'hidden',
        },
        textTitleProps: {
          family: 'medium',
          height: 'short',
          scale: '3',
        },
      };

    default: // text variant
      return {
        verticalSpacing: 0,
        sizes: { sm: 200 },
        textTitleProps: {
          setting: 'title-xsmall',
        },
      };
  }
};
/**
 * Render title for rebrand to add an icon arrow right if there is a link and the variant
 * is `editorial`. For all other cases render the title with its text properties.
 */
const renderTitle = (
  title: string,
  textTitleProps: TextProps,
  href: string,
  variant: CategoryCardVariant,
) => {
  if (!!href && variant === 'editorial') {
    return (
      <Inline spacing={DEFAULT_ICON_SPACING} vAlign="center">
        <Text {...textTitleProps}>{title}</Text>
        <IconArrowRight16 color="inherit" purpose="decorative" />
      </Inline>
    );
  }

  return <Text {...textTitleProps}>{title}</Text>;
};

/**
 * Presentational category card used by CategoryCard and the CategoriesCarousel end card.
 */
const CategoryCardBase = forwardRef(
  (
    {
      linkAs = 'a',
      image,
      title,
      description,
      tagline,
      href,
      onClick,
      cta,
      variant,
    }: CategoryCardBaseProps,
    ref?: Ref<HTMLDivElement>,
  ) => {
    const {
      copyStyles,
      imageStyles,
      verticalSpacing,
      sizes,
      textTitleProps,
      copyProps,
    } = variantLayoutBasedOnTheme(variant);
    const showCopy = [title, description, tagline, cta?.text].some(Boolean);

    return (
      <Stack
        ref={ref}
        spacing={verticalSpacing}
        data-testid={`category-card-${variant}`}
      >
        {!!image && (
          <Box
            aria-label={'altText' in image ? image.altText : title}
            as={linkAs}
            display="block"
            className={classNames(styles.image, styles[`image-${variant}`])}
            href={href}
            onClick={onClick}
          >
            <Box className={styles['image-container']} style={imageStyles}>
              {'mediaPath' in image ? (
                <CloudinaryImage
                  alt={image.altText}
                  baseUrl={image.baseUrl}
                  mediaPath={image.mediaPath}
                  sizes={sizes}
                />
              ) : (
                image
              )}
            </Box>
          </Box>
        )}
        {showCopy && (
          <Box className={styles['copy-container']}>
            <Box
              as={linkAs}
              display="block"
              href={href}
              onClick={onClick}
              className={styles['copy-container-link']}
              style={copyStyles}
              {...copyProps}
            >
              {!!title && renderTitle(title, textTitleProps, href, variant)}
              {!!description && <Text setting="body-small">{description}</Text>}
              {!!tagline && (
                <Text setting="body-small" color="gray-35">
                  {tagline}
                </Text>
              )}
            </Box>
            {!!cta && (
              <Box>
                <Button as="a" href={cta.href} onClick={onClick} variant="slim">
                  {cta.text}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Stack>
    );
  },
);

export default CategoryCardBase;
